import * as Sentry from '@sentry/vue';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { showBadgeOnFavicon } from './faviconHelper';
import { initFaviconSwitcher } from './faviconHelper';

import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants.js';
import GlobalStore from 'dashboard/store';
import AudioNotificationStore from './AudioNotificationStore';
import { getAssignee, isMessageFromCurrentUser } from './AudioMessageHelper';
import WindowVisibilityHelper from './WindowVisibilityHelper';
import { toast } from 'dashboard/helper/toast';
import i18n from 'dashboard/i18n/instance';

const NOTIFICATION_TIME = 30000;
const ALERT_DURATION = 10000;
const ALERT_PATH_PREFIX = '/audio/dashboard/';
const DEFAULT_TONE = 'ding';
const DEFAULT_ALERT_TYPE = ['none'];

export class DashboardAudioNotificationHelper {
  constructor(store) {
    if (!store) {
      throw new Error('store is required');
    }
    this.store = new AudioNotificationStore(store);

    this.notificationConfig = {
      audioAlertType: DEFAULT_ALERT_TYPE,
      playAlertOnlyWhenHidden: true,
      alertIfUnreadConversationExist: false,
    };

    this.recurringNotificationTimer = null;

    this.audioConfig = {
      audio: null,
      tone: DEFAULT_TONE,
      hasSentSoundPermissionsRequest: false,
    };

    this.currentUser = null;
    // Nomes de erro de play() já reportados nesta sessão (ver playAudioAlert).
    this.reportedPlayErrors = new Set();
  }

  intializeAudio = () => {
    const resourceUrl = `${ALERT_PATH_PREFIX}${this.audioConfig.tone}.mp3`;
    this.audioConfig.audio = new Audio(resourceUrl);
    return this.audioConfig.audio.load();
  };

  playAudioAlert = async () => {
    try {
      // `set` só carrega o áudio quando o tom MUDA em relação ao default —
      // usuário com o tom padrão ('ding') chegava aqui com audio=null e o
      // TypeError morria no catch abaixo, sem som e sem aviso.
      if (!this.audioConfig.audio) this.intializeAudio();
      await this.audioConfig.audio.play();
    } catch (error) {
      // Autoplay bloqueado (NotAllowedError) é política do navegador, não
      // defeito: todo play falha assim até o usuário interagir com a página.
      // Escolha deliberada: avisa o USUÁRIO uma vez (é ele quem resolve) e
      // não manda pro Sentry — seria um evento por mensagem de cada aba
      // aberta sem clique.
      if (error.name === 'NotAllowedError') {
        this.warnSoundBlocked();
        return;
      }
      this.reportPlayError(error);
    }
  };

  warnSoundBlocked = () => {
    if (this.hasSentSoundPermissionsRequest) return;
    this.hasSentSoundPermissionsRequest = true;
    toast.warning(
      i18n.global.t(
        'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.SOUND_PERMISSION_ERROR'
      ),
      { duration: ALERT_DURATION }
    );
  };

  // Qualquer outro erro é defeito nosso (mp3 404 → NotSupportedError, tom
  // inválido, AbortError, TypeError): o som parou e ninguém ficaria sabendo —
  // foi exatamente assim que o tom padrão ficou mudo sem ninguém perceber
  // (TypeError engolido por este catch). Console sempre; Sentry uma vez por
  // tipo de erro por sessão, porque o alerta recorrente de 30 s repetiria o
  // mesmo evento.
  reportPlayError = error => {
    // eslint-disable-next-line no-console
    console.error('[DashboardAudioNotificationHelper] play() falhou', error);
    if (this.reportedPlayErrors.has(error.name)) return;
    this.reportedPlayErrors.add(error.name);
    Sentry.captureException(error, { extra: { tone: this.audioConfig.tone } });
  };

  set = ({
    currentUser,
    alwaysPlayAudioAlert,
    alertIfUnreadConversationExist,
    audioAlertType = DEFAULT_ALERT_TYPE,
    audioAlertTone = DEFAULT_TONE,
  }) => {
    this.notificationConfig = {
      ...this.notificationConfig,
      audioAlertType: audioAlertType.split('+').filter(Boolean),
      playAlertOnlyWhenHidden: !alwaysPlayAudioAlert,
      alertIfUnreadConversationExist: alertIfUnreadConversationExist,
    };

    this.currentUser = currentUser;

    const previousAudioTone = this.audioConfig.tone;
    this.audioConfig = {
      ...this.audioConfig,
      tone: audioAlertTone,
    };

    if (previousAudioTone !== audioAlertTone) {
      this.intializeAudio();
    }

    initFaviconSwitcher();
    this.clearRecurringTimer();
    this.playAudioEvery30Seconds();
  };

  shouldPlayAlert = () => {
    if (this.notificationConfig.playAlertOnlyWhenHidden) {
      return !WindowVisibilityHelper.isWindowVisible();
    }
    return true;
  };

  executeRecurringNotification = () => {
    if (this.store.hasUnreadConversation() && this.shouldPlayAlert()) {
      this.playAudioAlert();
      showBadgeOnFavicon();
    }
    this.resetRecurringTimer();
  };

  clearRecurringTimer = () => {
    if (this.recurringNotificationTimer) {
      clearTimeout(this.recurringNotificationTimer);
    }
  };

  resetRecurringTimer = () => {
    this.clearRecurringTimer();
    this.recurringNotificationTimer = setTimeout(
      this.executeRecurringNotification,
      NOTIFICATION_TIME
    );
  };

  playAudioEvery30Seconds = () => {
    const { audioAlertType, alertIfUnreadConversationExist } =
      this.notificationConfig;

    //  Audio alert is disabled dismiss the timer
    if (audioAlertType.includes('none')) return;

    // If unread conversation flag is disabled, dismiss the timer
    if (!alertIfUnreadConversationExist) return;

    this.resetRecurringTimer();
  };

  shouldNotifyOnMessage = message =>
    this.shouldNotifyForAssignee(getAssignee(message));

  // Preferência "Alertas de áudio" do perfil (nenhum / atribuídas a mim / não
  // atribuídas / de outros) avaliada contra o atribuído da conversa. Serve o
  // alerta de mensagem nova e o de SLA estourado.
  shouldNotifyForAssignee = assigneeId => {
    const { audioAlertType } = this.notificationConfig;
    if (audioAlertType.includes('none')) return false;
    if (audioAlertType.includes('all')) return true;

    const assignedToMe = assigneeId === this.currentUser.id;
    const isUnassigned = !assigneeId;

    const shouldPlayAudio = [];

    if (
      audioAlertType.includes(EVENT_TYPES.ASSIGNED) ||
      audioAlertType.includes('mine')
    ) {
      shouldPlayAudio.push(assignedToMe);
    }
    if (audioAlertType.includes(EVENT_TYPES.UNASSIGNED)) {
      shouldPlayAudio.push(isUnassigned);
    }
    if (audioAlertType.includes(EVENT_TYPES.NOTME)) {
      shouldPlayAudio.push(!isUnassigned && !assignedToMe);
    }

    return shouldPlayAudio.some(Boolean);
  };

  onNewMessage = message => {
    // If the user does not have the permission to view the conversation, then dismiss the alert
    // FIX ME: There shouldn't be a new message if the user has no access to the conversation.
    if (!this.store.hasConversationPermission(this.currentUser)) {
      return;
    }

    // If the conversation status is pending, then dismiss the alert
    // This case is common for all audio event types
    if (this.store.isMessageFromPendingConversation(message)) {
      return;
    }

    // If the message is sent by the current user then dismiss the alert
    if (isMessageFromCurrentUser(message, this.currentUser.id)) {
      return;
    }

    if (!this.shouldNotifyOnMessage(message)) {
      return;
    }

    // If the message type is not incoming or private, then dismiss the alert
    const { message_type: messageType, private: isPrivate } = message;
    if (messageType !== MESSAGE_TYPE.INCOMING && !isPrivate) {
      return;
    }

    if (WindowVisibilityHelper.isWindowVisible()) {
      // If the user looking at the conversation, then dismiss the alert
      if (this.store.isMessageFromCurrentConversation(message)) {
        return;
      }

      // If the user has disabled alerts when active on the dashboard, the dismiss the alert
      if (this.notificationConfig.playAlertOnlyWhenHidden) {
        return;
      }
    }

    this.playAudioAlert();
    showBadgeOnFavicon();
    this.playAudioEvery30Seconds();
  };

  // SLA estourado (SlaMissedToaster): mesma preferência do alerta de mensagem
  // nova — tipo (contra o atribuído da conversa que estourou) e "só com a aba
  // em segundo plano". Sem badge nem repetição a cada 30s: o toast já fica
  // na tela até ser fechado. Devolve se tocou.
  onSlaMissed = conversation => {
    if (!this.shouldNotifyForAssignee(conversation?.meta?.assignee?.id)) {
      return false;
    }
    if (
      WindowVisibilityHelper.isWindowVisible() &&
      this.notificationConfig.playAlertOnlyWhenHidden
    ) {
      return false;
    }

    this.playAudioAlert();
    return true;
  };
}

export default new DashboardAudioNotificationHelper(GlobalStore);
